import { cn } from '@utils';
import * as React from 'react';
import type { SidebarLayoutProps } from './types';
import { SidebarPosition } from './types';

/**
 * SidebarLayout component
 * Renders a layout with a toggleable sidebar and main content area
 */
export const SidebarLayout: React.FC<SidebarLayoutProps> = React.memo(
	({
		sidebar,
		main,
		containerClassName = 'flex flex-col md:flex-row',
		sidebarClassName = 'w-full md:w-1/4',
		contentClassName = 'grow',
		sidebarPosition = SidebarPosition.LEFT,
		sidebarAriaLabel = 'Navigation Sidebar',
		contentAriaLabel = 'Main Content',
		isSidebarOpen = true,
	}) => {
		const renderSidebar = React.useCallback(
			() => (
				<div
					data-aid="sidebar"
					className={cn(
						sidebarClassName,
						'transition-all duration-300 ease-in-out',
						isSidebarOpen ? 'translate-x-0' : '-translate-x-full',
						isSidebarOpen ? 'block' : 'hidden'
					)}
					aria-label={sidebarAriaLabel}
					role="navigation"
				>
					{sidebar}
				</div>
			),
			[sidebar, sidebarClassName, sidebarAriaLabel, isSidebarOpen]
		);

		return (
			<div className={cn(containerClassName)}>
				{sidebarPosition === SidebarPosition.LEFT && isSidebarOpen && renderSidebar()}
				<div
					data-aid="main-content"
					className={cn(
						contentClassName,
						'transition-all duration-300 ease-in-out',
						isSidebarOpen ? 'md:w-3/4' : 'w-full'
					)}
					aria-label={contentAriaLabel}
					role="main"
				>
					{main}
				</div>
				{sidebarPosition === SidebarPosition.RIGHT && isSidebarOpen && renderSidebar()}
			</div>
		);
	}
);

SidebarLayout.displayName = 'SidebarLayout';
